import { useEffect, useState } from 'react'

import { Alert } from '@/components/ui'
import { useApi } from '@/hooks'
import { type AssetWindowState, AssetWindowNav, assetWindowParams } from '@/insights/common/assetHistoryWindow'
import { buildQuery } from '@/insights/common/queryUtils'
import { InsightStatusCode, isInsightStatus } from '@/types/dashboard-types'

import { LiveRouteMap, MapSkeleton, RouteSummary } from './LiveRouteMap'
import { useLiveRoute } from './liveRouteStore'
import RouteHistoryMap from './RouteHistoryMap'
import type { RouteHistory } from './types'

type Loaded =
  | { kind: 'loading' }
  | { kind: 'route'; route: RouteHistory }
  | { kind: 'empty' }
  | { kind: 'error'; message: string }

/**
 * The Route History insight for one asset, on the asset detail's Route Map tab. It opens on
 * today, following the asset live while it reports, and like the temperature graph it can step
 * back and forward a day at a time or show any From/To range. The period is shared with the
 * other date-driven tabs. Loads through the insight's own `load`, so it asks the API exactly
 * what a dashboard card would.
 */
export const AssetRouteHistory = ({
  companyId,
  assetId,
  assetWindow,
}: {
  companyId: number
  assetId: number
  assetWindow: AssetWindowState
}) => {
  const { api } = useApi()
  const { period } = assetWindow
  const [loaded, setLoaded] = useState<Loaded>({ kind: 'loading' })
  const params = assetWindowParams(companyId, assetId, period)
  const query = buildQuery(params)
  const id = `asset-sheet-route-${String(assetId)}`

  useEffect(() => {
    if (!api) return
    let cancelled = false
    setLoaded({ kind: 'loading' })
    void RouteHistoryMap.load(api, assetWindowParams(companyId, assetId, period)).then((result) => {
      if (cancelled) return
      if (!isInsightStatus(result)) {
        setLoaded({ kind: 'route', route: result })
      } else if (result.code === InsightStatusCode.NO_DATA) {
        setLoaded({ kind: 'empty' })
      } else {
        setLoaded({ kind: 'error', message: result.message ?? 'Could not load the route.' })
      }
    })
    return () => {
      cancelled = true
    }
  }, [api, companyId, assetId, period])

  const route = useLiveRoute(id, loaded.kind === 'route' ? loaded.route : undefined)

  return (
    <div className="flex h-full flex-col gap-2">
      <AssetWindowNav assetWindow={assetWindow} />
      <div className="min-h-0 flex-1">
        {loaded.kind === 'loading' && <MapSkeleton />}
        {loaded.kind === 'empty' && <Alert type="info" message="No location data for this period." />}
        {loaded.kind === 'error' && <Alert type="error" message={loaded.message} />}
        {loaded.kind === 'route' && <LiveRouteMap id={id} data={loaded.route} query={query} companyId={companyId} />}
      </div>
      {loaded.kind === 'route' && route && (
        <div className="text-sm text-base-content/70">
          <RouteSummary route={route} />
        </div>
      )}
    </div>
  )
}
